/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Roles list.
 *
 * A list first, then a detail page per role. The earlier master-detail split
 * put a 15-row permission grid in a narrow column beside a role list that is
 * usually four items long, which wasted the width where it was needed and gave
 * the list more prominence than it earns.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  AdminPanelSettings,
  ChevronRightRounded,
  ClearRounded,
  LockOutlined,
  SearchRounded,
  ShieldOutlined,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import { useCapability } from 'auth/usePermission';
import StyledAccessDenied from 'components/StyledAccessDenied';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

export default function RolesPermissions() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const canManage = useCapability('MANAGE_ROLES');

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    name: '', description: '', role_kind: 'base', clone_from_role_id: '',
  });

  const loadRoles = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/roles');
      setRoles(Array.isArray(data) ? data : []);
      setLoadError(null);
    } catch (error) {
      // "Not set up" and "not allowed" need different responses from whoever is
      // reading the page, so they must not both render as an empty list.
      setLoadError(
        error?.response?.status === 403
          ? 'You do not have permission to manage roles.'
          : 'Roles could not be loaded. Check that the backend is running.'
      );
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  // Client-side: this list is a handful of rows, so a request per keystroke
  // would add latency without adding anything.
  const visibleRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) =>
      `${r.name} ${r.description ?? ''} ${r.role_kind}`.toLowerCase().includes(q)
    );
  }, [roles, search]);

  const createRole = async () => {
    if (!draft.name.trim()) return;
    try {
      const { data } = await axiosInstance.post('/roles', {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        role_kind: draft.role_kind,
        capabilities: [],
        clone_from_role_id: draft.clone_from_role_id || null,
      });
      setCreating(false);
      setDraft({ name: '', description: '', role_kind: 'base', clone_from_role_id: '' });
      navigate(`/settings/roles/${data.id}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setToast({
        severity: 'error',
        message: typeof detail === 'string' ? detail : 'Could not create the role.',
      });
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <StyledAccessDenied />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        {isV2 ? (
          <PageHeader
            title="Roles & permissions"
            summary="Decide what each role can do, and whose records it can do it to."
            actions={
              <MDButton variant="gradient" color="info" onClick={() => setCreating(true)}>
                <AddRounded sx={{ mr: 0.75 }} /> New role
              </MDButton>
            }
          />
        ) : (
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={3} flexWrap="wrap">
            <Box>
              <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
                Roles &amp; permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Decide what each role can do, and whose records it can do it to.
              </Typography>
            </Box>
            <MDButton variant="gradient" color="info" onClick={() => setCreating(true)}>
              <AddRounded sx={{ mr: 0.75 }} /> New role
            </MDButton>
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !roles.length ? (
          <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
            <ShieldOutlined sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography variant="h6" gutterBottom>No roles yet</Typography>
            <Typography variant="body2" color="text.secondary">
              {loadError ?? 'Create a role to start controlling what people can see and do.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search roles"
              sx={{ maxWidth: 320, mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                      <ClearRounded sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {!visibleRoles.length ? (
              <Box sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '12px' }}>
                <Typography variant="body2" color="text.secondary">
                  No roles match &ldquo;{search}&rdquo;.
                </Typography>
              </Box>
            ) : (
            <Box display="grid" gap={1.25}>
              {visibleRoles.map((role) => (
                <Box
                  key={role.id}
                  component="button"
                  type="button"
                  onClick={() => navigate(`/settings/roles/${role.id}`)}
                  sx={{
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: { xs: '44px 1fr auto', md: '44px minmax(200px,320px) 1fr auto auto' },
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.75,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                    transition: 'border-color .15s, box-shadow .15s',
                    '&:hover': {
                      borderColor: 'info.main',
                      boxShadow: '0 2px 10px rgba(14,35,72,.07)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 38, height: 38, fontSize: 15, fontWeight: 700,
                      bgcolor: role.is_super ? 'warning.main' : 'rgba(21,101,200,0.10)',
                      color: role.is_super ? '#fff' : 'info.main',
                    }}
                  >
                    {role.is_super ? <AdminPanelSettings sx={{ fontSize: 20 }} /> : role.name[0]}
                  </Avatar>

                  <Box minWidth={0}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {role.name}
                      </Typography>
                      {role.is_system && (
                        <Tooltip title="Built-in role. Its permissions can be edited, but it cannot be renamed or deleted.">
                          <LockOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={role.role_kind === 'addon' ? 'Add-on role' : 'Base role'}
                      sx={{ height: 18, fontSize: 10.5, fontWeight: 600, mt: 0.25 }}
                      variant="outlined"
                      color={role.role_kind === 'addon' ? 'default' : 'info'}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: { xs: 'none', md: 'block' } }}
                    noWrap
                  >
                    {role.description || '—'}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                    {role.user_count} {role.user_count === 1 ? 'person' : 'people'}
                  </Typography>

                  <ChevronRightRounded sx={{ color: 'text.disabled' }} />
                </Box>
              ))}
            </Box>
            )}
          </>
        )}
      </MDBox>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Name" placeholder="e.g. Service Agent"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth size="small" label="Description (optional)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select fullWidth size="small" label="Type"
            value={draft.role_kind}
            onChange={(e) => setDraft({ ...draft, role_kind: e.target.value })}
            helperText={
              draft.role_kind === 'base'
                ? "Someone's main role. Everyone has exactly one."
                : 'An extra role that only widens access. People can hold several.'
            }
            sx={{ mb: 2 }}
          >
            <MenuItem value="base">Base role</MenuItem>
            <MenuItem value="addon">Add-on role</MenuItem>
          </TextField>
          <TextField
            select fullWidth size="small" label="Start from (optional)"
            value={draft.clone_from_role_id}
            onChange={(e) => setDraft({ ...draft, clone_from_role_id: e.target.value })}
            helperText="Copy another role's permissions as a starting point."
          >
            <MenuItem value="">Start with no access</MenuItem>
            {roles.filter((r) => !r.is_super).map((r) => (
              <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setCreating(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient" color="info" onClick={createRole}
            disabled={!draft.name.trim()}
          >
            Create role
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
